import { useMemo } from "react";
import { SchedulingService } from "../types/newReportTypes";

export type GroupedServices = {
  serviceTypeCode: number;
  serviceTypeName: string;
  services: SchedulingService[];
};

type ServiceTypeLabels = {
  [key: number]: string;
};

type UseSchedulingDataParams = {
  schedule: SchedulingService[];
  serviceTypeLabels: ServiceTypeLabels;
};

type UseSchedulingDataReturn = {
  weeklyGroups: GroupedServices[];
  quarterlyGroups: GroupedServices[];
};

/**
 * Custom hook to process and group scheduling services by type
 * Separates services into weekly (schedulingType: 1) and quarterly (schedulingType: 2) groups
 * Within each, groups by serviceTypeCode
 */
export const useSchedulingData = ({
  schedule,
  serviceTypeLabels,
}: UseSchedulingDataParams): UseSchedulingDataReturn => {
  // Group services by scheduling type and serviceTypeCode
  const groupServicesByType = useMemo(() => {
    return (schedulingType: 1 | 2): GroupedServices[] => {
      const filteredServices = schedule.filter(
        (service) => service.schedulingType === schedulingType
      );

      const grouped = filteredServices.reduce((acc, service) => {
        const code = service.serviceTypeCode;
        if (!acc[code]) {
          acc[code] = [];
        }
        acc[code].push(service);
        return acc;
      }, {} as Record<number, SchedulingService[]>);

      return Object.entries(grouped).map(([code, services]) => ({
        serviceTypeCode: Number(code),
        serviceTypeName:
          serviceTypeLabels[Number(code) as keyof typeof serviceTypeLabels] ||
          "Other",
        services,
      }));
    };
  }, [schedule, serviceTypeLabels]);

  const weeklyGroups = useMemo(
    () => groupServicesByType(1),
    [groupServicesByType]
  );
  const quarterlyGroups = useMemo(
    () => groupServicesByType(2),
    [groupServicesByType]
  );

  return {
    weeklyGroups,
    quarterlyGroups,
  };
};
